import type { AddToCartFormData } from "~/AddToCart/hooks/useAddToCartNew";
import { ProductService } from "~/AddToCart/ProductService";
import { bulkAddToCartApi } from "~/AddToCart/redux/addToCart/actions";
import { selectNetPrice } from "~/feature/ProductDescriptor/v1/productDescriptorUtils";
import { getStore } from "~/redux/getStore";
import { send as clearCartApi } from "~/Cart/api/ClearCart";

export interface SimpleAddToCartItem {
  productCode: string;
  packCode: string;
  quantity: number;
  priceReference: string;
  rdd?: Date | null;
  isSale: boolean;
  comment?: string;
}

export type SimpleAddToCartOptions = Parameters<typeof bulkAddToCartApi>[0]["context"] & {
  isOverride?: boolean;
  showSuccessDrawer?: boolean;
};

export async function simpleAddToCart(
  items: SimpleAddToCartItem[],
  { isOverride, showSuccessDrawer = true, ...options }: SimpleAddToCartOptions,
) {
  const transformedItems = await transformElements(items);

  if (isOverride) {
    await clearCartApi();
  }

  return bulkAddToCartApi({
    items: transformedItems,
    restrictionQuery: "",
    showSuccessDrawer,
    context: options,
  });
}

export async function transformElements(items: SimpleAddToCartItem[]) {
  const qtyPerProductCode = items.reduce(
    (acc, item) => {
      acc[item.productCode] = (acc[item.productCode] || 0) + item.quantity;
      return acc;
    },
    {} as Record<string, number>,
  );

  const transformedItems = await Promise.all(
    items.map(async (item) => {
      return transformElement({ ...item, totalQtyInRequest: qtyPerProductCode[item.productCode] });
    }),
  );

  return transformedItems;
}

type TargetType = AddToCartFormData & { productCode: string; rdd?: Date };

async function transformElement(item: SimpleAddToCartItem & { totalQtyInRequest: number }): Promise<TargetType> {
  // Await so that the data is added to store
  await ProductService.getProductDescriptor(item.productCode, item.priceReference, {
    fetch: true,
  }).promise!;

  const netPrice = selectNetPrice({
    productCode: item.productCode,
    packCode: item.packCode,
    priceReference: item.priceReference,
    extraQuantity: item.totalQtyInRequest,
  })(getStore().getState());

  return {
    productCode: item.productCode,
    quantity: item.quantity,
    priceReference: item.priceReference,
    selectedPackCode: item.packCode,
    selectedPack: null,
    netPrice: netPrice,
    surcharge: netPrice.surcharge,
    rdd: item.rdd ?? undefined,
    tariffSurcharge: netPrice.tariffSurcharge,
    priceUnit: netPrice.unit,
    totalQuantity: item.quantity,
    optionalParts: {}, // TODO...
    salePriceEnabled: item.isSale,
    comment: item.comment,
  };
}
